import { useState } from 'react';
import type { FieldMetric } from '@ederest/contracts';
import { displayFieldLabel } from '../../shared/ui/field-label.js';
import { Button } from '../../shared/ui/Button.js';
import { FrictionScore } from '../../shared/ui/FrictionScore.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import { LowSampleBadge } from '../../shared/ui/LowSampleBadge.js';

interface FieldMetricsTableProps {
  items: readonly FieldMetric[];
  totalCount: number;
  onRowClick?: (row: FieldMetric) => void;
}

/** Rows shown before the reader asks for the whole table. */
const PREVIEW_ROWS = 10;

const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;
const seconds = (ms: number) => (ms === 0 ? '—' : `${(ms / 1000).toFixed(ms >= 10_000 ? 0 : 1)} s`);

/**
 * Indicators per field (functional §5.5): median time, hesitations, re-entries,
 * errors, abandons, score. The API returns every measured field, ranked by
 * friction; the table opens on the ten that matter and on the fields measured
 * on enough sessions, and expands on request.
 */
export function FieldMetricsTable({ items, totalCount, onRowClick }: FieldMetricsTableProps) {
  const [expanded, setExpanded] = useState(false);
  const [withLowConfidence, setWithLowConfidence] = useState(false);

  const trusted = items.filter((item) => !item.lowConfidence);
  const lowConfidenceCount = items.length - trusted.length;
  const eligible = withLowConfidence ? items : trusted;
  const rows = expanded ? eligible : eligible.slice(0, PREVIEW_ROWS);

  const columns: Array<Column<FieldMetric>> = [
    {
      key: 'field',
      header: 'Champ',
      render: (row) => (
        <div className="cell-primary">
          <div>
            <div className="cell-primary__label">
              {displayFieldLabel(row.fieldLabel, row.fieldKey)} {row.lowConfidence && <LowSampleBadge />}
            </div>
            <div className="cell-primary__sub">
              {row.journeyName} · {row.stepName}
              {row.fieldLabel && <span className="cell-primary__key"> · {row.fieldKey}</span>}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'sessions',
      header: 'Usagers',
      align: 'end',
      render: (row) => row.focusedSessions.toLocaleString('fr-FR'),
    },
    { key: 'median', header: 'Temps médian', align: 'end', render: (row) => seconds(row.medianDurationMs) },
    {
      key: 'hesitations',
      header: 'Hésitations',
      align: 'end',
      render: (row) => row.hesitationCount.toLocaleString('fr-FR'),
    },
    { key: 'reentry', header: 'Reprises', align: 'end', render: (row) => percent(row.reentryRate) },
    { key: 'errors', header: 'Erreurs', align: 'end', render: (row) => percent(row.errorRate) },
    { key: 'abandon', header: 'Abandon', align: 'end', render: (row) => percent(row.abandonRate) },
    {
      key: 'score',
      header: 'Friction /100',
      render: (row) => <FrictionScore score={row.frictionScore} />,
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.stepId}:${row.fieldKey}`}
        onRowClick={onRowClick}
        empty="Aucun champ mesuré pour les filtres choisis."
      />
      <div className="dash__table-foot">
        <p className="dash__note">
          {rows.length} {rows.length > 1 ? 'champs affichés' : 'champ affiché'} sur {totalCount}, du plus
          difficile au plus simple.
          {lowConfidenceCount > 0 &&
            ` ${lowConfidenceCount} ${
              lowConfidenceCount > 1 ? 'champs mesurés' : 'champ mesuré'
            } sur trop peu d'usagers ${withLowConfidence ? 'sont affichés' : 'sont masqués'}.`}
        </p>
        <div className="dash__table-actions">
          {lowConfidenceCount > 0 && (
            <Button variant="ghost" onClick={() => setWithLowConfidence((value) => !value)}>
              {withLowConfidence
                ? 'Masquer les effectifs insuffisants'
                : 'Afficher les effectifs insuffisants'}
            </Button>
          )}
          {eligible.length > PREVIEW_ROWS && (
            <Button variant="ghost" onClick={() => setExpanded((value) => !value)}>
              {expanded ? `Réduire aux ${PREVIEW_ROWS} premiers` : `Voir les ${eligible.length} champs`}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
